// mainColor 派生链的纯函数集合 —— fix round 1。
//
// 特意零依赖（不 import defaultConfiguration / i18n 等任何东西）：这样才能被
// `node --test` 直接加载做纯逻辑测试，不用拉起 webpack/babel 整条链路
// （defaultConfiguration 那份配置对象要 import ../i18n，i18n 目录本身不是能被
// 纯 Node ESM resolve 的单文件，会直接 ERR_UNSUPPORTED_DIR_IMPORT）。
//
// 背景：mainColor 管线本身是通的（dash 配色能一路下发到 theme.mainColor，见
// widgetConfigToThemeUpdate），但商户在 dash 只填 main_color / user_bubble_color
// 这两个字段，不会去填 header_gradient——header 渐变、发送键、输入框 focus 环
// 这些"复合"视觉元素之前各有各的出厂蓝硬编码/静态默认值，谁都不读 mainColor，
// 于是明明 mainColor 已经是绿的，这几处纹丝不动。这里补的是"这些复合值在没有
// 各自的显式配置时，应该跟着 mainColor 走"这条派生规则。

export const FALLBACK_MAIN_COLOR = '#2563eb';

// 历史静态默认值，逐字节保留 —— fix round 2。round 1 把它删掉换成
// deriveHeaderGradient(mainColor) 全量计算，对没配过颜色的存量租户也生效：
// darkenHex('#2563eb', 15) = '#1f54c8'，比历史第二色标 '#3b82f6'（其实比主色更
// *亮*，不是调暗版）更暗——所有从没碰过 dash 配色的存量租户，header 渐变在这次
// 上线的瞬间集体变暗，是一次没人要求的视觉回归。派生规则只应该在 mainColor 真的
// 被改过时才接管；"压根没配过"必须精确复现原始像素，不能靠公式凑近似。
export const FACTORY_HEADER_GRADIENT = 'linear-gradient(135deg, #2563eb, #3b82f6)';

// dash WidgetConfig 页写的 settings.widget_config（snake_case，服务端字段名）
// → widget theme 状态（camelCase，defaultConfiguration 的字段名）映射表。
// 两条下发路径都用这份映射：/v1/usage/start 的 config.widget_config（widget.js
// applyConfig）、/v1/chat 的 appConfig.widget_config（widget.js preInitClient 早期
// 上色）。单一映射表，两条路径不会因为各写一份而慢慢跑偏。
export const WIDGET_CONFIG_THEME_KEY_MAP = {
    main_color: 'mainColor',
    // header_gradient 不在这张直传表里——它要经过 resolveHeaderGradient() 的
    // 三级优先级（customizations 显式 > 服务端显式 > 从 mainColor 派生），不能
    // 像其它字段一样"有就直传"，否则拿不到"服务端没传 header_gradient 时要不要
    // 派生"这个判断所需的原始 wc.header_gradient 值。resolveHeaderGradient 自己
    // 会读 wc.header_gradient，见下方。
    chat_bubble_color: 'chatBubbleColor',
    user_bubble_color: 'userBubbleColor',
    user_bubble_text_color: 'userBubbleTextColor',
    chat_bubble_text_color: 'chatBubbleTextColor',
    header_text_color: 'headerTextColor',
    font_family: 'fontFamily',
    border_radius: 'borderRadius',
    button_size: 'buttonSize',
    // 启动器图标风格（'duotone'|'classic'）。直传即可 —— 归一在渲染端
    // （chat-floating-button 调 launcherGlyph()，脏值回落出厂风格），
    // 与 button_size 的"服务端钳一次、客户端再钳一次"是同一条纪律。
    launcher_icon_style: 'launcherIconStyle',
    launcher_icon_color: 'launcherIconColor',
    placeholder_text: 'placeholderText',
    header_mode: 'headerMode',
    response_time_text: 'responseTimeText',
    header_pattern: 'headerPattern',
    show_header_avatar: 'showHeaderAvatar',
    home_page_enabled: 'homePageEnabled',
    show_list_button: 'showListButton',
    list_button_position: 'listButtonPosition',
    // 导航简化（2026-08）：tab 里是否显示 Home（默认关，见 renderTabs 的 hasHome）+
    // 左上角导航按钮显示方式（always 常驻 / hover 悬停，默认 always，见
    // _navDisplayAlways / renderListButton 的显隐门控）。三个键（+home_page_enabled）
    // 正交组成导航配置，契约与 native 三端一致，见 task-nav-simplify-report。
    show_home_tab: 'showHomeTab',
    header_nav_display: 'headerNavDisplay',
    // 首次访问落地视图（'home'|'chat'，只在 home_page_enabled=true 时有意义）。
    // 见 resolveFirstVisitLandingView。
    initial_view: 'initialView',
    // 暗色主题：商户总开关（默认关，false 也要透传——widget 端用 undefined 区分
    // "服务端还没说话"和"服务端说了关"）、模式（light|dark|auto）、暗色配色对象
    // （缺键运行时用 deriveDarkPalette 兜底，见 resolveDarkPalette）。
    dark_mode_enabled: 'darkModeEnabled',
    theme_mode: 'themeMode',
    dark_colors: 'darkColors',
    // Launcher label, Home layout, offline status. False must pass through as well, because
    // undefined and "turned off" read differently once a config has been cached.
    launcher_label_enabled: 'launcherLabelEnabled',
    launcher_label_text: 'launcherLabelText',
    launcher_label_show_response_time: 'launcherLabelShowResponseTime',
    launcher_label_mobile: 'launcherLabelMobile',
    home_layout: 'homeLayout',
    offline_status_mode: 'offlineStatusMode',
};

/**
 * 判定 obj[key] 是否算"集成方真的显式设置过"——fix round 2：光有
 * hasOwnProperty 不够。`{ mainColor: someUnsetVariable }` 这种写法在
 * window.laneChatCustomizations 里非常容易出现（模板拼对象、变量没赋值），
 * key 是有的，值是 undefined/null/''，语义上跟"压根没写这个 key"应该一样——
 * 不该拿它去挡服务端下发的正常值。跟 wc 那侧（`wc[srcKey] === undefined ||
 * null || ''` 才跳过）、resolveHeaderGradient（`typeof ... === 'string' &&
 * explicit.headerGradient` 真值检查）已经在用的防御是同一个标准，这里补齐。
 */
export function isExplicitlySet(obj, key) {
    if (!obj || typeof obj !== 'object') return false;
    if (!Object.prototype.hasOwnProperty.call(obj, key)) return false;
    const v = obj[key];
    return v !== undefined && v !== null && v !== '';
}

/**
 * 把 settings.widget_config（服务端下发的外观配置）映射成 theme 状态的增量更新。
 *
 * 优先级：集成方在 window.laneChatCustomizations 里显式设置过的字段永远赢
 * （embed 方明确要什么颜色就是什么颜色）；服务端下发的值只填集成方没管过的字段；
 * 都没有就维持 defaultConfiguration 的出厂色。之前 widget.js 的 applyConfig() 是
 * 无条件拿 wc.* 覆盖 theme，集成方显式传的 mainColor 反而会被 dash 配置盖掉——
 * 这里顺带把优先级修对。
 *
 * @param {object} wc settings.widget_config（原样透传，未必已清洗，缺字段跳过）
 * @param {object} customizations window.laneChatCustomizations 原始对象（不是合并后的
 *   props.conf——必须是原始对象，才能用 isExplicitlySet 区分"集成方真的设置过"）
 * @returns {object} 可直接 setState 到 theme 上的增量对象（只含需要更新的键）
 */
export function widgetConfigToThemeUpdate(wc, customizations) {
    const update = {};
    if (!wc || typeof wc !== 'object') return update;
    const explicit = (customizations && typeof customizations === 'object') ? customizations : {};

    for (const [srcKey, themeKey] of Object.entries(WIDGET_CONFIG_THEME_KEY_MAP)) {
        if (wc[srcKey] === undefined || wc[srcKey] === null || wc[srcKey] === '') continue;
        if (isExplicitlySet(explicit, themeKey)) continue; // 集成方显式设置过，服务端不覆盖
        update[themeKey] = wc[srcKey];
    }

    // 数值/带单位字段：类型转换不能走上面那张直传表
    if (wc.desktop_width && !isExplicitlySet(explicit, 'desktopWidth')) {
        update.desktopWidth = parseInt(wc.desktop_width, 10);
    }
    if (wc.desktop_height && !isExplicitlySet(explicit, 'desktopHeight')) {
        update.desktopHeight = parseInt(wc.desktop_height, 10);
    }
    if (wc.button_position_bottom && !isExplicitlySet(explicit, 'btnPcBottom')) {
        update.btnPcBottom = wc.button_position_bottom + 'px';
    }
    if (wc.button_position_right && !isExplicitlySet(explicit, 'btnPcRight')) {
        update.btnPcRight = wc.button_position_right + 'px';
    }
    if (wc.header_agents && !isExplicitlySet(explicit, 'headerAgents')) {
        try {
            update.headerAgents = typeof wc.header_agents === 'string' ? JSON.parse(wc.header_agents) : wc.header_agents;
        } catch (e) { /* 脏 JSON，保留 defaultConfiguration 的 [] */ }
    }
    if (Array.isArray(wc.home_links) && !isExplicitlySet(explicit, 'homeLinks')) {
        update.homeLinks = wc.home_links;
    }
    if (Array.isArray(wc.home_blocks) && !isExplicitlySet(explicit, 'homeBlocks')) {
        update.homeBlocks = wc.home_blocks;
    }
    if (Array.isArray(wc.quick_buttons) && !isExplicitlySet(explicit, 'quickButtons')) {
        update.quickButtons = wc.quick_buttons;
    }
    if (wc.business_hours && typeof wc.business_hours === 'object') {
        update.businessHours = wc.business_hours;
    }
    // Clearing an image saves '', and the table above skips ''. Without this the image from
    // a cached config would stay until the next visit.
    if (typeof wc.launcher_image_url === 'string' && !isExplicitlySet(explicit, 'launcherImageUrl')) {
        update.launcherImageUrl = wc.launcher_image_url;
    }
    if (typeof wc.logo_url === 'string' && !isExplicitlySet(explicit, 'logoUrl')) {
        update.logoUrl = wc.logo_url;
    }

    return update;
}

/**
 * "尽早上色"更新的纯计算（bug 5：原生 WebView 里出厂蓝要很久才变成 dash 配的
 * 颜色，桌面浏览器却是瞬间）。从 widget.js preInitClient() 里抽出来，好处
 * 两个：
 *   1. node --test 能直接跑通两种到达顺序（数据先到 / mount 先到），不用
 *      拉起真的 React 组件；
 *   2. 调用方（preInitClient 数据到达时、componentDidMount 挂载时）都能
 *      调同一份逻辑判"现在有没有一份该应用但还没应用的早期主题"，不用各自
 *      重新实现一遍 widgetConfigToThemeUpdate + resolveHeaderGradient 的
 *      组合 —— 这正是之前那个 `this._isMounted` 闸门只在"数据到达"这一侧
 *      判断、"挂载"那一侧完全没有对应消费点的根源：两个到达顺序里只有一个
 *      被处理了。
 *
 * @param {object|null|undefined} appConfig /v1/chat 响应的 appConfig（含
 *   widget_config）；没有就返回 null，调用方不做任何 setState。
 * @param {object} customizations window.laneChatCustomizations（集成方显式
 *   设置优先级最高，同 widgetConfigToThemeUpdate/resolveHeaderGradient）。
 * @param {string} currentMainColor 当前 theme.mainColor —— headerGradient
 *   在 earlyWc 没给 mainColor 时用它兜底重新派生。
 * @returns {object|null} 可以直接丢进 setState 的 theme 增量；没有可应用的
 *   内容（appConfig 缺 widget_config，或映射出来是空对象）时返回 null。
 */
export function computeEarlyThemeUpdate(appConfig, customizations, currentMainColor) {
    if (!appConfig || typeof appConfig !== 'object' || !appConfig.widget_config) return null;
    const earlyWc = appConfig.widget_config;
    const earlyThemeUpdate = widgetConfigToThemeUpdate(earlyWc, customizations);
    if (Object.keys(earlyThemeUpdate).length === 0) return null;
    earlyThemeUpdate.headerGradient = resolveHeaderGradient(
        earlyThemeUpdate.mainColor || currentMainColor,
        earlyWc,
        customizations
    );
    return earlyThemeUpdate;
}

// ============================================================================
// 颜色解析（widget 侧唯一出口）—— 商户可以配带透明度的颜色之后补的。
//
// 背景：dash 的外观配置原本用 `<input type="color">`，那是浏览器原生取色器，
// **只吃 `#RRGGBB`、没有 alpha 通道**。换成自绘取色器后，main_color 等字段可能
// 是 `rgba(232, 95, 58, 0.5)` / `#e85f3a80` / `#fff` 这些形态，下面这条派生链
// （渐变、CSS 变量三元组、各处 tint/阴影）原本清一色假设"就是六位 hex"，要么
// 静默降级、要么直接吐出 `NaN, NaN, 55` 这种把 CSS var fallback 也一起废掉的
// 垃圾值。所有需要"拆开颜色算点什么"的地方都必须过这里。
//
// 只解析、不改写：合法值原样透传给 CSS 的场景（背景/边框/文字色）不需要经过
// 这里——rgba() 和 8 位 hex 本来就是合法 CSS。
// ============================================================================

const _clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const _round255 = (v) => _clamp(Math.round(v), 0, 255);

function _num(raw, scale) {
    const s = String(raw).trim();
    if (s === '') return null;
    if (s.endsWith('%')) {
        const n = Number(s.slice(0, -1));
        return Number.isFinite(n) ? (n / 100) * scale : null;
    }
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
}

/**
 * 任意颜色字符串 → `{ r, g, b, a }`（r/g/b 0-255 整数，a 0-1）。
 * 认 #RGB / #RGBA / #RRGGBB / #RRGGBBAA / rgb(...) / rgba(...)（逗号分隔与
 * CSS Color 4 的空格+斜杠分隔都认）。认不出来返回 null —— **不要**在这里兜底
 * 成某个默认色，调用方各有各的降级策略（有的原样透传给浏览器，有的放弃派生）。
 */
export function parseColor(input) {
    if (typeof input !== 'string') return null;
    const s = input.trim().toLowerCase();
    if (s === '') return null;

    const hex = s.match(/^#([0-9a-f]+)$/);
    if (hex) {
        const h = hex[1];
        if (h.length === 3 || h.length === 4) {
            const v = h.split('').map((c) => parseInt(c + c, 16));
            return { r: v[0], g: v[1], b: v[2], a: h.length === 4 ? v[3] / 255 : 1 };
        }
        if (h.length === 6 || h.length === 8) {
            return {
                r: parseInt(h.slice(0, 2), 16),
                g: parseInt(h.slice(2, 4), 16),
                b: parseInt(h.slice(4, 6), 16),
                a: h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1,
            };
        }
        return null; // 5 / 7 位是打字打到一半，不认
    }

    const fn = s.match(/^rgba?\(([^)]*)\)$/);
    if (fn) {
        const slash = fn[1].split('/');
        const parts = slash[0].split(/[\s,]+/).filter((p) => p !== '');
        if (parts.length < 3 || parts.length > 4) return null;
        const r = _num(parts[0], 255);
        const g = _num(parts[1], 255);
        const b = _num(parts[2], 255);
        if (r === null || g === null || b === null) return null;
        const aRaw = slash.length > 1 ? slash[1] : parts[3];
        let a = 1;
        if (aRaw !== undefined) {
            const parsed = _num(aRaw, 1);
            if (parsed === null) return null;
            a = _clamp(parsed, 0, 1);
        }
        return { r: _round255(r), g: _round255(g), b: _round255(b), a };
    }

    return null;
}

const _hex2 = (v) => _round255(v).toString(16).padStart(2, '0');

/**
 * `{ r, g, b, a }` → CSS 字符串。a>=1 时回 `#rrggbb`（跟存量配置同一种写法），
 * 否则回 `rgba(r, g, b, a)`。选 rgba() 不选 8 位 hex：`#RRGGBBAA` 在老 Android
 * WebView（原生 SDK 内嵌 widget 的运行环境）上会被整条声明丢弃，rgba() 无处不支持。
 */
export function formatColor({ r, g, b, a }) {
    if (a >= 1) return `#${_hex2(r)}${_hex2(g)}${_hex2(b)}`;
    return `rgba(${_round255(r)}, ${_round255(g)}, ${_round255(b)}, ${Math.round(_clamp(a, 0, 1) * 1000) / 1000})`;
}

/**
 * 任意颜色 → "r, g, b" 三元组（给 CSS `rgba(var(--widget-main-rgb), 0.1)` 这种
 * 用法用）。**alpha 在这里被有意剥掉**：消费方每条规则自己写死了要的透明度
 * （hover 底 8%、focus 环 20% …），把主色的 alpha 再乘进去只会让这些提示色淡到
 * 看不见。要的是"品牌色相"，不是"品牌色相 × 商户配的透明度"。
 * 认不出来返回 null，调用方自己决定兜底（通常是不写这个变量、让 CSS 侧的
 * `var(--x, 37, 99, 235)` fallback 生效）。
 */
export function colorToRgbTriplet(color) {
    const c = parseColor(color);
    return c ? `${c.r}, ${c.g}, ${c.b}` : null;
}

/**
 * #rrggbb → "r, g, b"。历史签名，严格只认六位 hex（老调用方/测试锁着这个契约）；
 * 新代码用 colorToRgbTriplet，它认所有形态。
 */
export function hexToRgbTriplet(hex) {
    if (typeof hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(hex.trim())) return null;
    return colorToRgbTriplet(hex);
}

/**
 * 把颜色调暗 percent%（0-100），**alpha 原样保留**。认不出来的输入原样返回——
 * 调用方传进来的可能已经是渐变/关键字之类不是纯色的东西，派生链不该在这种输入
 * 上崩，就地放弃派生、让上层继续用原值。
 */
export function darkenColor(color, percent) {
    const c = parseColor(color);
    if (!c) return color;
    const factor = 1 - (_clamp(percent, 0, 100) / 100);
    return formatColor({ r: c.r * factor, g: c.g * factor, b: c.b * factor, a: c.a });
}

/**
 * darkenColor 的六位 hex 专用版（历史签名，测试锁着"三位简写原样返回"的契约）。
 * 新代码用 darkenColor。
 */
export function darkenHex(hex, percent) {
    if (typeof hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(hex.trim())) return hex;
    return darkenColor(hex, percent);
}

/**
 * 换一个 alpha（0-1）。给"主色的 8% 淡底""主色的 40% 投影"这类派生用——
 * 以前是字符串拼接 `mainColor + '15'`，主色一旦不是六位 hex 就拼出非法 CSS，
 * 整条声明被浏览器丢掉（图标底/投影直接消失）。
 * 认不出来原样返回。
 */
export function withAlpha(color, alpha) {
    const c = parseColor(color);
    return c ? formatColor({ ...c, a: _clamp(alpha, 0, 1) }) : color;
}

/** 剥掉 alpha 得到不透明底色。认不出来原样返回。 */
export function stripAlpha(color) {
    const c = parseColor(color);
    return c ? formatColor({ ...c, a: 1 }) : color;
}

/** 两个颜色字符串是不是同一个颜色（`#2563EB` / `#2563eb` / `rgba(37,99,235,1)` 算同一个） */
export function sameColor(a, b) {
    const pa = parseColor(a);
    const pb = parseColor(b);
    if (!pa || !pb) return a === b;
    return formatColor(pa) === formatColor(pb);
}

/**
 * header 渐变的派生公式：主色 → 主色的 15% 调暗版，135deg 对角线。
 * 用 darkenColor 而不是 darkenHex：主色带 alpha 时（`rgba(232,95,58,.5)`）也要
 * 派生出一条真的有明暗差的渐变，而不是两个一模一样的色标拼出来的"平的渐变"。
 * alpha 由 darkenColor 原样带过去——半透明主色的 header 就该是半透明的。
 */
export function deriveHeaderGradient(mainColor) {
    return `linear-gradient(135deg, ${mainColor}, ${darkenColor(mainColor, 15)})`;
}

/**
 * The Home layout's softer gradient: brand color easing to a slightly deeper tone, top to
 * bottom, so the hero reads calmer than the diagonal default. It deepens instead of
 * lightening because the title on the hero stays white and needs the contrast.
 * The dash preview mirrors this (softHeaderGradient in WidgetConfig.vue).
 */
export function softHeaderGradient(mainColor) {
    return `linear-gradient(180deg, ${mainColor}, ${darkenColor(mainColor, 6)})`;
}

/**
 * headerGradient 的完整优先级解析（customizations 显式 > 服务端显式 > 从
 * resolvedMainColor 派生）。特意直接吃「原始」customizations 对象和「原始」
 * wc（settings.widget_config），不吃合并/累积过的 theme 或 themeUpdate 对象——
 * 合并后的对象一律"看起来有 headerGradient 这个 key"（default 兜底/上一次
 * setState 留下的值），没法用 hasOwnProperty 可靠区分"这次真的显式传了"还是
 * "只是继承了之前的值"。
 *
 * 出厂路径（第三级，派生之前）：resolvedMainColor 就是 FALLBACK_MAIN_COLOR 本身
 * ——即客户端/服务端都没把 mainColor 改成别的颜色——直接返回历史静态字面量
 * FACTORY_HEADER_GRADIENT，逐字节等于改动前的默认值，不经过 darkenHex 计算。
 * 这不是"性能优化少算一次"，是必须的：darkenHex('#2563eb',15) 算出来的值和历史
 * 字面量的第二色标不是同一个颜色（历史那个反而更亮，不是调暗版），存量租户一个
 * 字节都不该变。只有 mainColor 真的被 customizations/服务端改过时，才轮到
 * deriveHeaderGradient 接管、按新颜色算一条新渐变。
 *
 * @param {string} resolvedMainColor 调用方已经按优先级算好的最终 mainColor
 * @param {object|null} wc 本次要应用的 settings.widget_config（没有就传 null）
 * @param {object} customizations window.laneChatCustomizations 原始对象
 */
/**
 * Home 导航门控（唯一出口）：租户没开 Home 首页时，任何"跳去 Home"的请求都必须
 * 降级成会话列表，而不是强行渲出被商户关掉的视图。原本只有 handleListButtonClick
 * 一处这么判（`theme.homePageEnabled` 真值检查），native-bridge navigate
 * {view:'home'}（fix round 1）新增第二处同语义调用点——抽成纯函数，两处共用同一
 * 判断，不会各写一份慢慢跑偏。真值检查（不是 `!== false && !== 0` 那种"未设置就
 * 当启用"的宽松判定，那是 _headerRowMetrics 等尺寸模型用的另一套语义，跟"是否可
 * 导航过去"是两回事）——未配置 homePageEnabled 时默认关闭 Home 入口。
 * @param {*} homePageEnabled theme.homePageEnabled 原始值（未必是严格 boolean）
 * @returns {'home'|'chatList'}
 */
export function resolveHomeViewAction(homePageEnabled) {
    return homePageEnabled ? 'home' : 'chatList';
}

/**
 * 首次访问落地视图（唯一判定出口，跟 resolveHomeViewAction 同一个家族——那个管
 * "点了 Home 入口该不该真的去 Home"，这个管"面板第一次展开该落在哪个视图"）。
 *
 * 优先级（从高到低）：
 *   1. hasExplicitChatTarget — 深链 chat_id（widget.html ?view=chat&chat_id=N /
 *      native-bridge navigate {chatId}），或者 ?view=chat 本身不带 chat_id
 *      （widget-index.js 的 forceChatView prop）。集成方/推送明确要落地聊天
 *      视图，不管 Home 配置怎么写，必须直接进 Chat——这不是"要不要看 Home"的
 *      问题。
 *   2. hasCachedChat — 本地已经缓存过 chat_id（chat.js _getCachedChatId 用的
 *      同一个 localStorage 键 `lane_chatid_${appId}`），代表这不是真正的
 *      "首次访问"，是回访且有过对话——必须直接回到那次对话，不受 Home 配置
 *      影响（需求 2 的"回访保持现状"）。
 *   3. 都不是 → 真·首次访问：homePageEnabled 为假 → chat（Home 入口都没开，
 *      落地页更不该是 Home）；homePageEnabled 为真 → 按 initialView 配置
 *      （'chat' 显式选了 Chat；其它任何值——包括未配置——都落 Home，这是
 *      dash 侧的出厂默认，配置面板不出现时也一样）。
 *
 * 纯函数，不摸 DOM/localStorage 自己——调用方（Widget 构造函数用同步的本地
 * 缓存判断，preInitClient/applyConfig 配置到达后用服务端下发的
 * homePageEnabled/initialView 重新判一次）负责收集这些输入。
 *
 * @param {object} params
 * @param {boolean} params.hasExplicitChatTarget
 * @param {boolean} params.hasCachedChat
 * @param {*} params.homePageEnabled theme.homePageEnabled 原始值（未必是严格 boolean）
 * @param {*} params.initialView theme.initialView 原始值（未必已校验）
 * @returns {'home-widget'|'chat-widget'}
 */
/**
 * 商户配的「自动打开聊天窗口」该不该在这次加载触发。
 *
 * 这个开关此前是**四端零消费**：dash 有开关、落库、/v1/chat 照发
 * (`appConfig.auto_open`)，而 `grep -rn "auto_open\|autoOpen" app/frontend/src sdk/`
 * 零命中 —— 商户勾了保存成功、回显正确，访客端从来不弹。
 *
 * 只对**浮层 web widget** 有意义，所以 inline 直接否：inline 没有面板可开，
 * 原生 SDK 的容器本身就是"已打开"，让 widget 在里面再自动开一次没有语义。
 * 这一点写在这里而不是散在调用点，是为了让"为什么原生不消费它"有个明确答案，
 * 而不是又变成一处沉默的缺口。
 *
 * 每个会话只自动开一次：访客手动关掉之后，同一次浏览里不该再被弹回来。
 */
export function resolveAutoOpen({ autoOpen, inline, isChatOpen, openedThisSession }) {
    if (!autoOpen || autoOpen === '0' || autoOpen === 'false') return false;
    if (inline) return false;          // 没有面板可开
    if (isChatOpen) return false;      // 已经开着
    if (openedThisSession) return false;
    return true;
}

export function resolveFirstVisitLandingView({ hasExplicitChatTarget, hasCachedChat, homePageEnabled, initialView }) {
    if (hasExplicitChatTarget || hasCachedChat) return 'chat-widget';
    if (!homePageEnabled) return 'chat-widget';
    return initialView === 'chat' ? 'chat-widget' : 'home-widget';
}

/**
 * 直达聊天入口（widget.html ?view=chat&chat_id=<N> / native-bridge navigate
 * 事件 payload.chatId）共用的校验：raw → 正整数 chat_id 或 null。
 * 纯语法校验（正整数）——是否真的属于当前访客由服务端在 loadMessages 时兜底
 * （不存在/不属于该访客 → 'Chat not found'，渲出空会话而不是崩溃），这里不做
 * 也做不到那层归属校验。
 * @param {string|number|null|undefined} raw
 * @returns {number|null}
 */
export function parsePositiveIntId(raw) {
    if (raw === null || raw === undefined || raw === '') return null;
    const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
    if (!Number.isFinite(n) || n <= 0 || Math.trunc(n) !== n) return null;
    return n;
}

export function resolveHeaderGradient(resolvedMainColor, wc, customizations) {
    const explicit = (customizations && typeof customizations === 'object') ? customizations : {};
    if (typeof explicit.headerGradient === 'string' && explicit.headerGradient) {
        return explicit.headerGradient;
    }
    // Above the saved header_gradient because the dash always saves one: below it, the
    // option could never take effect.
    if (wc && typeof wc === 'object' && wc.home_soft_gradient === true) {
        return softHeaderGradient(resolvedMainColor || FALLBACK_MAIN_COLOR);
    }
    if (wc && typeof wc === 'object' && typeof wc.header_gradient === 'string' && wc.header_gradient) {
        return wc.header_gradient;
    }
    const mc = resolvedMainColor || FALLBACK_MAIN_COLOR;
    // sameColor 而不是 ===：`#2563EB` / `rgba(37, 99, 235, 1)` 是同一个出厂蓝，
    // 只是写法不同，都该走"逐字节复现历史渐变"这条路，不该因为大小写或写法差异
    // 掉进派生分支、把存量租户的 header 悄悄改暗。半透明的主色（a<1）不会命中，
    // 走派生 —— 那正是它该做的。
    if (sameColor(mc, FALLBACK_MAIN_COLOR)) {
        return FACTORY_HEADER_GRADIENT;
    }
    return deriveHeaderGradient(mc);
}

// ============================================================================
// [退役 2026-08 —— 见下方 resolveColorKnown] 关闭态浮动按钮(FAB) 渲染门控，
// fix round 3 引入。当时的分析是"configReady true 时颜色早就该确定了，这道
// 闸门在正常路径下几乎不会真的触发"——把它当成防御性的边缘场景兜底，而不是
// 主防线。用户后续又报告了三轮"仍有概率出现默认蓝"，根因排查后发现：
//   1. 这道闸门只挂在"关闭态圆形 FAB"这一个分支上——closedStyle==='chat'、
//      isChatOpen、wasChatOpened() 时改走的 header 渲染分支（renderLargeHeader
//      /renderCompactHeader/renderDefaultHeader）完全没有等价保护，autoChatOpen
//      /深链/teaser 点击/SW 推送点开这些"面板一上来就是打开态"的入口全部绕过。
//   2. fallbackFired（4s 客户端超时）本身就违反"工厂默认色只有一种合法出场
//      方式：服务端明确确认过'该商户无自定义配置'"这条不变量——超时不是服务端
//      确认，是客户端放弃等待，之前却直接把这当成"颜色已解决"处理，把出厂蓝
//      当真实答案画出来。
// 结论：不是这道闸门本身写错了，是它的应用范围和"超时=已解决"这个判据都是
// 局部补丁，不是结构性不变量。下面的 resolveColorKnown 是新的唯一决策出口，
// widget.js render() 只在两个渲染入口（inline/浮动）各查一次，覆盖 FAB、
// header 全部三种、teaser、消息内容——不再要求"新增一个品牌色渲染点时记得
// 也接一下这道闸"。
// ============================================================================

// ============================================================================
// 暗色主题引擎 —— 全产品唯一算法真源（widget dark theme）。
//
// dash 侧「自动填充暗色配色」按钮移植的就是这份 deriveDarkPalette；运行时
// dark_colors 缺键的兜底也走它。输出必须确定性（同输入恒同输出），由
// test/dark-theme.test.mjs 锁定 —— 改公式必须同步改测试 + dash 移植版。
//
// 公式原则（用户契约）：
//   · 品牌色相保留 —— accent 只动饱和度/亮度，不动 hue；
//   · 降亮度适度去饱和 —— s×0.85，l 收进 [0.38, 0.60]（过亮的品牌色压下来，
//     过暗的抬起来保证在深底上可见）；
//   · 表面色（面板/卡片/背景）用中性深灰系常量，不做"品牌色加深"——品牌色
//     染底在暗色下非常容易显脏；
//   · 文字色对比度守 WCAG AA（4.5:1）：优先保留亮色侧的文字色，不达标时在
//     白/近黑里挑对比度更高的那个。
//
// 模式解析（resolveThemeMode）：暗色是商户显式 opt-in ——
//   dark_mode_enabled=false（含所有存量商户）时永远 'light'，theme URL 参数、
//   laneChatCustomizations.theme、prefers-color-scheme 全部忽略；开了之后
//   优先级：集成方显式（jssdk customizations.theme / widget.html ?theme=）
//   > 商户 widget_config.theme_mode > 'light'。'auto' 跟随系统。
// ============================================================================

export const THEME_MODES = ['light', 'dark', 'auto'];

// ---------- 系统主题提示（sys_theme，原生 SDK 契约） ----------
//
// Android WebView 的 prefers-color-scheme 不跟系统 uiMode，跟宿主 Activity
// 主题的 isLightTheme（targetSdk 33+ 行为）——LaneChat 原生容器是亮色主题，
// 媒体查询在 App 内恒 false，auto 档在 App 里永远解析成亮。修法：原生 SDK
// 在 widget URL 上**永远**附加 `sys_theme=dark|light`（容器用平台标准 API
// 解析到的真实系统主题），auto 档优先吃这个提示，没有提示才回落
// prefers-color-scheme（网页浏览器场景媒体查询是可靠的）。
// 注意语义：sys_theme 只喂 auto 分支，不参与 resolveThemeMode 的优先级链，
// 永远盖不过商户/集成方显式 light/dark。

export const SYS_THEME_HINTS = ['light', 'dark'];

/**
 * sys_theme 宽进归一：trim + lowercase，白名单外一律 null（= 无提示）。
 * @returns {'light'|'dark'|null}
 */
export function normalizeSysThemeHint(raw) {
    const v = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    return SYS_THEME_HINTS.indexOf(v) !== -1 ? v : null;
}

// 模块级提示位：widget-index 启动时从 window.laneChatSysTheme（widget.html
// ?sys_theme= 白名单解析）透传进来（setSysThemeHint），widget.js 的
// resolveEffectiveTheme 调用零改动即生效（默认参数每次调用求值）。
// 每次页面加载静态一份——原生系统主题运行时变化按"重开生效"处理（最小实现，
// 实时切换列增强项）。
let _sysThemeHint = null;

/** 设置系统主题提示（widget-index 启动透传唯一入口）。非法值清空。 */
export function setSysThemeHint(raw) {
    _sysThemeHint = normalizeSysThemeHint(raw);
}

/** 当前系统主题提示，'light'|'dark'|null。 */
export function getSysThemeHint() {
    return _sysThemeHint;
}

/**
 * 模式解析唯一出口。
 * @param {object} params
 * @param {*} params.darkModeEnabled widget_config.dark_mode_enabled（商户总开关，默认关）
 * @param {*} params.explicitTheme 集成方显式设置（laneChatCustomizations.theme；
 *   widget.html ?theme= 白名单解析后也写进同一个字段）
 * @param {*} params.serverThemeMode widget_config.theme_mode（商户 dash 配置）
 * @returns {'light'|'dark'|'auto'}
 */
export function resolveThemeMode({ darkModeEnabled, explicitTheme, serverThemeMode }) {
    if (!darkModeEnabled) return 'light';
    if (THEME_MODES.indexOf(explicitTheme) !== -1) return explicitTheme;
    if (THEME_MODES.indexOf(serverThemeMode) !== -1) return serverThemeMode;
    return 'light';
}

/**
 * 模式 → 当次实际生效主题。'auto' 档的输入优先级：sysThemeHint（原生容器
 * 提示，见上方 SYS_THEME_HINTS 注释；缺省取模块级 setSysThemeHint 存的值）
 * > prefers-color-scheme 实时值（widget.js 挂 matchMedia change 监听，
 * 变化时重算——有原生提示时该重算无效果，提示恒胜）。
 * @returns {'light'|'dark'}
 */
export function resolveEffectiveTheme(mode, prefersDark, sysThemeHint = _sysThemeHint) {
    if (mode === 'dark') return 'dark';
    if (mode === 'auto') {
        const hint = normalizeSysThemeHint(sysThemeHint);
        if (hint) return hint;
        return prefersDark ? 'dark' : 'light';
    }
    return 'light';
}

// ---------- 颜色数学（HSL / 对比度），deriveDarkPalette 的内部依赖 ----------

function _rgbToHsl({ r, g, b }) {
    const rn = r / 255, gn = g / 255, bn = b / 255;
    const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
    const l = (max + min) / 2;
    if (max === min) return { h: 0, s: 0, l };
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0));
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    return { h: h * 60, s, l };
}

function _hue2rgb(p, q, t) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
}

function _hslToRgb({ h, s, l }) {
    if (s === 0) {
        const v = l * 255;
        return { r: v, g: v, b: v };
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const hn = ((h % 360) + 360) % 360 / 360;
    return {
        r: _hue2rgb(p, q, hn + 1 / 3) * 255,
        g: _hue2rgb(p, q, hn) * 255,
        b: _hue2rgb(p, q, hn - 1 / 3) * 255,
    };
}

/** WCAG 相对亮度（0-1）。认不出来的输入返回 null。 */
export function relativeLuminance(color) {
    const c = parseColor(color);
    if (!c) return null;
    const chan = (v) => {
        const n = v / 255;
        return n <= 0.03928 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * chan(c.r) + 0.7152 * chan(c.g) + 0.0722 * chan(c.b);
}

/** WCAG 对比度（1-21）。任一颜色认不出来返回 null。 */
export function contrastRatio(a, b) {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    if (la === null || lb === null) return null;
    const hi = Math.max(la, lb), lo = Math.min(la, lb);
    return (hi + 0.05) / (lo + 0.05);
}

// 中性深灰系常量（slate 深色端微调）。表面色不从品牌色派生 —— 见文件顶部原则。
// surface_alt / surface_deep / text_muted 是运行时专用 token（hover 底、骨架块、
// 三级文字），不进 dark_colors 可配键 —— 可配面收在 DARK_COLOR_KEYS 那 12 个。
export const DARK_NEUTRALS = {
    background_color: '#0f1115',      // 页面/消息区底
    surface_color: '#1a1e26',         // 面板/composer/卡片/弹层
    surface_alt_color: '#242936',     // 次级填充（hover 底/轨道/agent 气泡默认）
    surface_deep_color: '#2e3444',    // 骨架块/更深的占位填充
    border_color: '#2e3340',
    text_color: '#e8eaf0',
    text_secondary_color: '#9aa3b4',
    text_muted_color: '#717a8c',
};

/**
 * 品牌色 → 暗色版 accent：色相不动，s×0.85，l 收进 [0.38, 0.60]，alpha 原样。
 * 认不出来的输入原样返回（同 darkenColor 的降级姿态）。
 */
export function deriveDarkAccent(color) {
    const c = parseColor(color);
    if (!c) return color;
    const hsl = _rgbToHsl(c);
    const rgb = _hslToRgb({
        h: hsl.h,
        s: hsl.s * 0.85,
        l: Math.min(0.60, Math.max(0.38, hsl.l)),
    });
    return formatColor({ ...rgb, a: c.a });
}

/**
 * 底色上挑文字色：preferred 达 WCAG AA（4.5:1）就保留；不达标在
 * 白 / 近黑（背景深灰）里挑对比度更高的那个。
 */
export function pickTextOn(bg, preferred) {
    if (preferred && (contrastRatio(preferred, bg) || 0) >= 4.5) return preferred;
    const white = '#ffffff';
    const dark = DARK_NEUTRALS.background_color;
    return (contrastRatio(white, bg) || 0) >= (contrastRatio(dark, bg) || 0) ? white : dark;
}

/**
 * 底色上挑**图标**色。与 pickTextOn 的唯一区别是阈值：3:1 而不是 4.5:1。
 *
 * 依据 WCAG 1.4.11 Non-text Contrast：图形元素（图标、控件边界）的门槛是 3:1，
 * 文字才是 4.5:1。用文字的阈值去判图标会把**中间调品牌色全部逼成近黑** ——
 * 珊瑚 #E85F3A 上白色是 3.5:1，合规且是所有同类产品的做法，可 4.5:1 判它不合格。
 * 2026-08-09 我就是这么把启动器图标改黑的，用户当场看出来了。
 *
 * 真正需要翻深色的只有 #f5f5f5 / #eab308 这类**本身就很亮**的底色 —— 那时白色
 * 连 3:1 都过不了，是真看不见。
 */
export function pickIconOn(bg, preferred) {
    if (preferred && (contrastRatio(preferred, bg) || 0) >= 3) return preferred;
    const white = '#ffffff';
    const dark = DARK_NEUTRALS.background_color;
    return (contrastRatio(white, bg) || 0) >= (contrastRatio(dark, bg) || 0) ? white : dark;
}

// dark_colors 可配键（与服务端 sanitize 白名单、dash 配置面一致，不可改名）。
// 键名与亮色 widget_config 平行（main_color/header_gradient/…_bubble_color），
// 亮色没有的表面/文字键（background/surface/border/text*）为暗色专有。
export const DARK_COLOR_KEYS = [
    'main_color', 'header_gradient', 'header_text_color',
    'background_color', 'surface_color', 'border_color',
    'text_color', 'text_secondary_color',
    'chat_bubble_color', 'chat_bubble_text_color',
    'user_bubble_color', 'user_bubble_text_color',
];

/**
 * 推导公式唯一真源：亮色配色 → 完整暗色配色（12 键全给）。
 * @param {object} light 亮色侧已解析的配色（snake_case，同 widget_config 键名）：
 *   { main_color, user_bubble_color?, user_bubble_text_color?, header_text_color? }
 *   缺键用出厂亮色兜底。
 * @returns {object} DARK_COLOR_KEYS 全键的暗色配色
 */
export function deriveDarkPalette(light = {}) {
    const mainLight = light.main_color || FALLBACK_MAIN_COLOR;
    const main = deriveDarkAccent(mainLight);
    const userBubble = deriveDarkAccent(light.user_bubble_color || mainLight);
    // header：暗色下用「暗 accent 再压暗」的深渐变，保品牌色相、观感收敛。
    const headerG1 = darkenColor(main, 20);
    const headerG2 = darkenColor(main, 42);
    return {
        main_color: main,
        header_gradient: `linear-gradient(135deg, ${headerG1}, ${headerG2})`,
        header_text_color: pickTextOn(headerG1, light.header_text_color || '#ffffff'),
        background_color: DARK_NEUTRALS.background_color,
        surface_color: DARK_NEUTRALS.surface_color,
        border_color: DARK_NEUTRALS.border_color,
        text_color: DARK_NEUTRALS.text_color,
        text_secondary_color: DARK_NEUTRALS.text_secondary_color,
        chat_bubble_color: DARK_NEUTRALS.surface_alt_color,
        chat_bubble_text_color: DARK_NEUTRALS.text_color,
        user_bubble_color: userBubble,
        user_bubble_text_color: pickTextOn(userBubble, light.user_bubble_text_color || '#ffffff'),
    };
}

// header_gradient 的客户端侧防线（服务端 sanitizeWidgetAppearance 是第一道）：
// 只认 linear/radial-gradient 且不含 url()/表达式注入所需的字符。
const _GRADIENT_RE = /^(?:linear|radial)-gradient\([#0-9a-zA-Z,.%\s()-]+\)$/;

/**
 * 运行时暗色配色解析：商户 dark_colors 覆盖 > deriveDarkPalette 推导兜底。
 * 逐键校验 —— 非法值（解析不了的颜色 / 夹带注入的渐变）静默落回推导值。
 */
export function resolveDarkPalette(lightPalette, darkColors) {
    const derived = deriveDarkPalette(lightPalette);
    if (!darkColors || typeof darkColors !== 'object') return derived;
    const out = { ...derived };
    for (const key of DARK_COLOR_KEYS) {
        const v = darkColors[key];
        if (typeof v !== 'string' || v.trim() === '') continue;
        const val = v.trim();
        if (key === 'header_gradient') {
            if (val.length <= 512 && _GRADIENT_RE.test(val) && val.toLowerCase().indexOf('url(') === -1) {
                out[key] = val;
            }
        } else if (parseColor(val)) {
            out[key] = val;
        }
    }
    return out;
}

// ============================================================================
// 亮色语义色板（native UI P0 T1，spec §5.2 步骤 1-2）。
//
// 历史问题（盘点风险 #2）：暗色 token 有权威定义（darkPaletteToCssVars），
// 亮色没有 —— --lc-* 在亮色下全部 removeProperty，靠各调用点**互不相同**的
// var(--lc-x, fallback) 兜底（--lc-surface 三种写法、--lc-border ≥4 值、
// --lc-text 5 值）。原生三端若各自造表必然与 web 全面细微色偏，「token 级
// 100% 还原」在亮色下无定义可言。这里为每个 token 钦定唯一规范值，亮色下
// 也**恒定义**全部 --lc-*（widget.js _syncThemeCssVars 消费），各调用点的
// fallback 自然失效但**保持原样不动**（避免大面积模板串手术）。
//
// 钦定值 = 现调用点众数（spec §5.2 步骤 1 初值 + 盘点 §3.1 表逐 token 比对，
// 2026-08-07 实测 grep 定稿）。众数之外的调用点从此表取值 —— 由此产生的
// 已知色差（#f8f9fa→#f8fafc 量级）逐处白名单登记于 T1 任务报告：
//   --lc-bg        #f8fafc（11 处众数；≠ #f8f9fa×4 / #fbfcfd×2 / #f9fafb×2 / #fafbfc / #f1f5f9 / #f0f2f5）
//   --lc-surface   #ffffff（46 处 #fff/#ffffff/white 同色，无色差）
//   --lc-surface-2 #f1f5f9（≠ #f0f0f0×7 / #f8fafc×6 / #f5f5f5×4 / #f3f4f6×4 / #f3f3f3×3 / #f9fafb / #f8f8f8）
//   --lc-surface-3 #e2e8f0（3 处众数；≠ #e5e9ef×2 / #f0f0f0 / #ddd / #cbd5e1）
//   --lc-text      #1e293b（13 处众数；≠ #333×7 / #111827×5 / #334155×4 / #212529 / #0f172a）
//   --lc-text-2    #64748b（slate 系钦定；≠ #666×19 / #475569×8 / #6b7280×4 / #4b5563×4 / #6c757d×2 / #555 / #444 / #374151）
//   --lc-text-3    #94a3b8（15 处众数；≠ #999×9 / #9ca3af×3 / #cbd5e1 / #888）
//   --lc-border    #e5e7eb（10 处众数；≠ #e2e8f0×9 / #f1f5f9×8 / #e0e0e0×6 / #eef2f7×5 / #f0f0f0×4 / #ddd×3 / #e9ecef×2 / #d1d5db×2 / #cbd5e1×2 / #f3f4f6）
//   气泡/滚动条/系统消息/emoji 三元组：与既有唯一（或全体一致的）fallback
//   逐字节相同，无色差。
//
// --lc-bubble-user-bg 是对 --widget-main-color 的 var() 引用而非字面量：
// user 气泡必须随品牌色，钦定成 #2563eb 会把自定义主色租户钉死成出厂蓝。
//
// 【流程闸】改此表 = 改三端亮色语义 token：必须重跑 `npm run tokens:export`
// 同步 sdk/shared/design-tokens.json，并逐处更新上面的白名单注释。
// ============================================================================
/**
 * 终局初始化失败时浮球的填充色 —— 中性，绝不是猜出来的品牌色。
 *
 * colorKnown 不变式说的是"没有合法来源就不要画品牌色"。初始化终局失败时
 * **永远不会有**那个来源了，所以正确答案不是继续什么都不画（那样浮球不出现、
 * 面板打不开、错误页和重试按钮永远够不着），而是用与错误页同族的中性灰画出来。
 * 取 surface-3，与骨架块同色 —— 骨架同样是"刻意无品牌色"的那一档。
 */
/**
 * 浮球图标色。商户配了就用,没配就是**白**。
 *
 * 刻意不按对比度自动挑色。自动版(pickIconOn)在无障碍上是对的,但商户从没碰过
 * 这一项却看到图标在浅色品牌色上自己变黑,换个品牌色又跳回白 —— 不可预期。
 * 用户裁定(2026-08-10):默认白,要别的自己配。对比度提示放在 dash 配置侧,
 * 那里可以告诉商户"这个组合看不清",而不是替他把颜色改掉。
 *
 * 脏值(非法色值/空串/非字符串)一律回落白色,不做猜测。
 */
export function launcherIconColor(configured) {
    if (typeof configured !== 'string') return '#ffffff';
    const v = configured.trim();
    if (!v) return '#ffffff';
    return parseColor(v) ? v : '#ffffff';
}

export function neutralLauncherColor(_isDark) {
    // 亮暗都用**深**中性色,不跟随主题。
    //
    // 第一版亮色下取 surface-3(#e2e8f0)。浮球图标的颜色是算出来的
    // (pickIconOn:优先白,低于 3:1 才退深色),而白色压在 #e2e8f0 上只有约
    // 1.1:1 —— 于是图标被迫变黑。用户报的"jssdk 唤起按钮的 icon 怎么是黑的"
    // 就是它:浅底逼出深图标,而错误态浮球长得又跟正常浮球一样,看不出是错误。
    //
    // 深中性色下白图标合规(见 pickIconOn),而且一个深灰圆看着是刻意的中性态,
    // 不像"配色坏掉的正常按钮"。它仍然不是猜出来的品牌色,colorKnown 不变式没破。
    return DARK_NEUTRALS.surface_deep_color;
}

export const LIGHT_PALETTE = Object.freeze({
    '--lc-bg': '#f8fafc',
    '--lc-surface': '#ffffff',
    '--lc-surface-2': '#f1f5f9',
    '--lc-surface-3': '#e2e8f0',
    '--lc-text': '#1e293b',
    '--lc-text-2': '#64748b',
    '--lc-text-3': '#94a3b8',
    '--lc-border': '#e5e7eb',
    '--lc-bubble-agent-bg': '#f1f5f9',
    '--lc-bubble-agent-text': '#1e293b',
    '--lc-bubble-user-bg': 'var(--widget-main-color, #2563eb)',
    '--lc-bubble-user-text': '#ffffff',
    '--lc-scrollbar': 'rgba(0, 0, 0, 0.15)',
    '--lc-scrollbar-hover': 'rgba(0, 0, 0, 0.25)',
    '--lc-system-msg-bg': 'rgba(0, 0, 0, 0.04)',
    '--lc-emoji-bg-rgb': '255, 255, 255',
    '--lc-emoji-text-rgb': '30, 41, 59',
    '--lc-emoji-input-rgb': '241, 245, 249',
});

/**
 * 亮色语义色板 → CSS 变量表（--lc-*），与 darkPaletteToCssVars **同名同序**
 * （对称性由 test/light-palette.test.mjs 锁定）。亮色没有可配的表面/文字键
 * （盘点 §3.2：亮色仅 6 纯色+1 渐变，全走 --widget-main-color 那条线），
 * 所以无参数：输出恒为钦定表。返回浅拷贝，调用方可安全增删键。
 */
export function lightPaletteToCssVars() {
    return { ...LIGHT_PALETTE };
}

/**
 * 暗色配色 → 组件层消费的 CSS 变量表（--lc-*）。亮色对称表 LIGHT_PALETTE /
 * lightPaletteToCssVars 在上方 —— 自 T1（2026-08-07）起亮色也恒定义全部
 * --lc-*，调用点的 var(--lc-x, fallback) 中 fallback 不再触发。
 */
export function darkPaletteToCssVars(p) {
    const vars = {
        '--lc-bg': p.background_color,
        '--lc-surface': p.surface_color,
        '--lc-surface-2': DARK_NEUTRALS.surface_alt_color,
        '--lc-surface-3': DARK_NEUTRALS.surface_deep_color,
        '--lc-text': p.text_color,
        '--lc-text-2': p.text_secondary_color,
        '--lc-text-3': DARK_NEUTRALS.text_muted_color,
        '--lc-border': p.border_color,
        '--lc-bubble-agent-bg': p.chat_bubble_color,
        '--lc-bubble-agent-text': p.chat_bubble_text_color,
        '--lc-bubble-user-bg': p.user_bubble_color,
        '--lc-bubble-user-text': p.user_bubble_text_color,
        // 深底上黑色滚动条/黑色系统消息底看不见 —— 换白色系低透明度
        '--lc-scrollbar': 'rgba(255, 255, 255, 0.16)',
        '--lc-scrollbar-hover': 'rgba(255, 255, 255, 0.26)',
        '--lc-system-msg-bg': 'rgba(255, 255, 255, 0.06)',
    };
    // emoji-mart 吃的是 "r, g, b" 三元组变量。解析不出来（防御性分支：生产路径
    // 上 p 恒经 resolveDarkPalette 逐键校验，实际到不了）就落回 DARK_NEUTRALS
    // 三元组 —— 宁可用中性深灰，不吐 NaN。T1 前这里是"不写键让亮色 fallback
    // 生效"；亮色恒定义后键集合必须与 LIGHT_PALETTE 恒同构（对称性单测锁定），
    // 且暗色面板里落回深灰也比落回一块亮白更对。
    vars['--lc-emoji-bg-rgb'] = colorToRgbTriplet(p.surface_color)
        || colorToRgbTriplet(DARK_NEUTRALS.surface_color);
    vars['--lc-emoji-text-rgb'] = colorToRgbTriplet(p.text_color)
        || colorToRgbTriplet(DARK_NEUTRALS.text_color);
    vars['--lc-emoji-input-rgb'] = colorToRgbTriplet(DARK_NEUTRALS.surface_alt_color);
    return vars;
}

// ============================================================================
// 结构层设计 token（spec docs/superpowers/specs/2026-08-08-design-system-tokens.md）
//
// 与上面的**主题层**（LIGHT_PALETTE / darkPaletteToCssVars，颜色，商户可配）
// 正交：结构层是形状/间距/阴影几何/动效时长曲线，**全局统一、商户不可配、
// 禁止字面量**。两层互不知情 —— --lc-radius-md 不认识 --lc-bubble-user-bg。
//
// 为什么放这里：theme-color.js 是零依赖纯函数模块，既能被 shadow DOM 注入
// 路径 import，也能被 scripts/export-design-tokens.mjs 直接 `node` 加载 ——
// 于是 web 与三端原生共享同一份真源（design-tokens.json 的 structure 段）。
//
// 与 colorKnown 闸门（reference_widget_color_gate）的关系：结构 token **不等
// 任何配置**，静态 <style> 首帧即注入（structureTokensCssText）。附带收益是
// 启动骨架屏能用与真界面同一批圆角/间距画，交接不跳变。
//
// 【流程闸】改这三张表 = 改三端结构 token：必须重跑 `npm run tokens:export`
// 同步 sdk/shared/design-tokens.json（native 侧按同一 JSON 实施）。
// ============================================================================

// ---- Radius（spec §2）：4 档 + 胶囊 + 2 个气泡语义特例 ----------------------
// 收敛来源：2/3/4/5→xs，6/8/10(小件)→sm，10(大件)/12/14→md，16/18/20/22→lg，
// 999/50%→full。气泡尾角是聊天界面的类型语义（不是随手值），保留不收敛。
export const RADIUS_SCALE = Object.freeze({
    '--lc-radius-xs': '4px',
    '--lc-radius-sm': '8px',
    '--lc-radius-md': '12px',
    '--lc-radius-lg': '16px',
    '--lc-radius-full': '999px',
    // 语义特例：消息气泡。尾角朝向发送方 —— in = 对方（尾角左下），
    // out = 自己（尾角右下）。两端一致即可，不参与 4 档收敛。
    '--lc-radius-bubble-in': '18px 18px 18px 4px',
    '--lc-radius-bubble-out': '18px 18px 4px 18px',
});

// ---- Spacing（spec §3）：4 基数 6 档 ----------------------------------------
// 分块规则：同组内 space-2(8) / 组内小节 space-3(12) / 跨组区块 space-4(16) /
// 大区块 space-6(24)。相邻触控目标间隙 ≥ space-2。
export const SPACE_SCALE = Object.freeze({
    '--lc-space-1': '4px',
    '--lc-space-2': '8px',
    '--lc-space-3': '12px',
    '--lc-space-4': '16px',
    '--lc-space-6': '24px',
    '--lc-space-8': '32px',
});

// ---- Shadow（spec §4）：3 档 elevation ---------------------------------------
// 阴影色**恒用中性 slate(15,23,42)，不吃品牌色** —— 商户配红色主题时阴影跟着
// 变红是错的。暗色只换色值/alpha，几何（offset/blur）逐字节不变。
// 历史怪值 `#8e8d8d -3px 2px 20px`、两套色系混用（rgba(0,0,0,.1) vs
// rgba(15,23,42,.08)）在此统一。
export const SHADOW_LIGHT = Object.freeze({
    '--lc-shadow-1': '0 1px 2px rgba(15, 23, 42, 0.06)',
    '--lc-shadow-2': '0 4px 12px rgba(15, 23, 42, 0.10)',
    '--lc-shadow-3': '0 8px 24px rgba(15, 23, 42, 0.16)',
});
export const SHADOW_DARK = Object.freeze({
    '--lc-shadow-1': '0 1px 2px rgba(0, 0, 0, 0.45)',
    '--lc-shadow-2': '0 4px 12px rgba(0, 0, 0, 0.50)',
    '--lc-shadow-3': '0 8px 24px rgba(0, 0, 0, 0.60)',
});

// ---- Motion（spec §5）：3 档时长 + 曲线 --------------------------------------
// 入场 ease-out / 出场 ease-in，且**出场比入场快一档**（slow 入 → base 出）：
// 这是"跟手"的关键。spring 是既有的刻意回弹（FAB/浮动按钮按压），保留成
// token 而不是散落字面量。UI 动效上限 500ms。
export const MOTION_SCALE = Object.freeze({
    '--lc-dur-fast': '150ms',
    '--lc-dur-base': '200ms',
    '--lc-dur-slow': '300ms',
    '--lc-ease-out': 'cubic-bezier(0.16, 1, 0.3, 1)',
    '--lc-ease-in': 'cubic-bezier(0.7, 0, 0.84, 0)',
    '--lc-ease-spring': 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    // 循环动效专用（裁决 R14）。ease-out/ease-in 是**有方向**的一次性曲线：
    // 入场减速到位、出场加速离场。骨架呼吸、typing 弹跳这类**往复循环**没有
    // 方向可言,两头都要软 —— 强行拿方向曲线拼会得到一头硬一头软的跛脚循环。
    //
    // 值 = CSS `ease-in-out` 关键字的规范定义 cubic-bezier(0.42, 0, 0.58, 1)。
    // 刻意不另造一条"更有个性"的对称曲线:web 现有的骨架/typing 调用点写的就是
    // `ease-in-out` 关键字,取关键字等价值 → 本次改动**零像素变化**,纯粹是把
    // 字面量收进 token;同时 Compose 的库存 `EaseInOut` 也正是这四个控制点,
    // Android 那两处(TypingDots 拆成 easeOut+easeIn、SkeletonScreen 直接用库存
    // EaseInOut,即审计点名的"周期一致但曲线没走 token")改过来同样零变化。
    //
    // 纪律:这一档**只给循环/往复**。入场出场仍必须走 ease-out / ease-in,
    // 否则"出场比入场快一档 + 方向感"那条就没了。
    '--lc-ease-in-out': 'cubic-bezier(0.42, 0, 0.58, 1)',
});

// ---- 分隔线（spec §3）：几何取结构层(1px solid)，颜色取主题层(--lc-border) --
// 两层在这一个 shorthand 里正交组合 —— 商户换主题色时线跟着变色，粗细恒定。
// 纪律：能用间距表达的分组**不要加线**，线是最后手段。
export const HAIRLINE_TOKEN = Object.freeze({
    // 不能写成 'var(--lc-hairline)' —— 那是自引用循环,CSS 计算期判定整条
    // invalid,结果是**全部 40 余处发丝线不渲染**(2026-08-08 交叉审计实测该
    // 变量返回空串)。正确形态:几何(1px solid)写死在这里,颜色引主题层的
    // --lc-border(商户换主题跟着变色,粗细恒定),正交组合成一条 shorthand。
    '--lc-hairline': '1px solid var(--lc-border, #e5e7eb)',
});

// ---- 强调色别名（跨端规范 §4.3「主色遵从(硬性)」）---------------------------
// 与 HAIRLINE_TOKEN 同一门派：结构层负责把**主题层的品牌色**包装成可直接写进
// 声明的形态,自身不含任何品牌色字面量。真源永远是 --widget-main-color /
// --widget-main-rgb（widget.js _syncThemeCssVars 按当前显示主题写在 shadow
// host 上,暗色下写的已是推导后的暗色主色）—— 所以这几个别名**天然亮暗自适应**,
// 不需要进 LIGHT_PALETTE/暗色表的亮暗对称体系。
//
// 为什么需要:审计发现富消息卡里散落 #2563eb / #9C27B0 等硬编码强调色 —— 商户
// 把主色配成橙色,卡片还是蓝的紫的,直接暴露"这块没接主题"。调用点直接写
// `var(--widget-main-color, #2563eb)` 也能跑,但那等于把出厂蓝复制到几十处,
// 换回落色要改几十个地方;收成别名后只有这里一处。
//
// alpha 档位取现调用点众数:0.082 = 历史十六进制后缀 15(图标托盘/徽标底),
// 0.09 = 富按钮软填充(与 Android RichButton 的 mainColor@.09 同值),
// 0.35 = 富按钮描边(与 Android 的 mainColor@.35 同值)。
export const ACCENT_TOKEN = Object.freeze({
    '--lc-main': 'var(--widget-main-color, #2563eb)',
    '--lc-main-rgb': 'var(--widget-main-rgb, 37, 99, 235)',
    '--lc-main-tint': 'rgba(var(--widget-main-rgb, 37, 99, 235), 0.082)',
    '--lc-main-soft': 'rgba(var(--widget-main-rgb, 37, 99, 235), 0.09)',
    '--lc-main-outline': 'rgba(var(--widget-main-rgb, 37, 99, 235), 0.35)',
    // 进度填充(裁决 R8)。此前是 material 成功绿 `#4CAF50` / 渐变
    // `linear-gradient(90deg, #4CAF50, #81C784)` —— **进度不是成功态**,把"传了
    // 62%"画成绿色在语义上就是错的(真正的成功态是传完之后那条消息本身),而且
    // 商户配橙色主色时这条绿杠格外突兀,是"这块没接主题"的活证据。
    //
    // 尾端不另找一个颜色,而是让主色**朝轨道底色淡出**:轨道是 --lc-surface-3,
    // 于是亮色下尾端偏浅、暗色下尾端偏深,两种主题各自成立 —— 这正是硬编码
    // `#81C784`(只在浅底上说得通)做不到的。手法与上面三个别名同宗:alpha 铺在
    // --widget-main-rgb 上,不在这里手算任何色值。
    // 0.65 取自历史那对绿的关系(#81C784 ≈ #4CAF50 兑约 35% 白),换主色后观感等价。
    '--lc-main-gradient': 'linear-gradient(90deg, var(--lc-main), rgba(var(--widget-main-rgb, 37, 99, 235), 0.65))',
});

// ---- 状态色（跨端规范 §2.5「已登记的刻意例外」）-----------------------------
// 这三个是**唯一**允许不吃品牌色的语义色:成功/警告/危险表达的是系统对操作的
// 判断,不是品牌表达 —— 商户把主色配成红色时,"提交失败"也必须还能读成失败。
// 亮暗恒定(与既有的发送失败红 #e53e3e 同一处置),所以和强调色别名一样不进
// 亮暗对称表。
//
// 纪律:这张表**只增不散**。组件里再出现新的绿/黄/红字面量,先问它是不是这三
// 类之一;是就用 token,不是就说明它其实该跟主色。
export const STATUS_TOKEN = Object.freeze({
    // 既有发送失败红,继续沿用(存量像素契约)
    '--lc-danger': '#e53e3e',
    // 绿:取 slate 邻域的 green-600。历史用的 #4CAF50(material)在浅底上偏亮,
    // 当正文色对比度不足;#16a34a 与 --lc-text 系同族更协调。
    '--lc-success': '#16a34a',
    // 黄:amber-500。历史用的 #FF9800(material)同样偏亮。
    '--lc-warning': '#f59e0b',
});

/**
 * 结构层 token 全集 → CSS 变量表。阴影取**亮色**档（首帧默认；暗色由
 * shadowTokensToCssVars 在主题解析后覆写，几何不变只换色）。
 */
export function structureTokensToCssVars() {
    return {
        ...RADIUS_SCALE,
        ...SPACE_SCALE,
        ...SHADOW_LIGHT,
        ...MOTION_SCALE,
        ...HAIRLINE_TOKEN,
        ...ACCENT_TOKEN,
        ...STATUS_TOKEN,
    };
}

/**
 * 暗/亮阴影档（只有这 3 个结构 token 随主题变）。widget.js _syncThemeCssVars
 * 在解析出 __dark 后写到 shadow host 内联样式上，覆盖静态 <style> 的亮色默认。
 */
export function shadowTokensToCssVars(dark) {
    return { ...(dark ? SHADOW_DARK : SHADOW_LIGHT) };
}

/**
 * 结构层 token 的静态 CSS 文本（首帧注入，不等任何配置）。
 * @param {string} selector ':host'（shadow root 内）或 ':root'（宿主文档/
 *   独立 chat.html 页面）。
 *
 * 同时输出两段附加规则：
 *   1. 暗色下的阴影覆写（宿主文档路径靠 <html data-lc-theme="dark">；shadow
 *      路径由 _syncThemeCssVars 内联覆写，这条规则在 shadow 内不匹配也无害）。
 *   2. prefers-reduced-motion 降级 —— 三档时长一律压成 1ms，于是**所有**用
 *      var(--lc-dur-*) 写的过渡/动画统一瞬切，不用逐处判断（spec §5 无障碍）。
 */
export function structureTokensCssText(selector = ':host') {
    const decl = (vars, indent = '    ') => Object.entries(vars)
        .map(([k, v]) => `${indent}${k}: ${v};`)
        .join('\n');
    return `
${selector} {
${decl(structureTokensToCssVars())}
}
${selector}[data-lc-theme="dark"] {
${decl(SHADOW_DARK)}
}
@media (prefers-reduced-motion: reduce) {
    ${selector} {
        --lc-dur-fast: 1ms;
        --lc-dur-base: 1ms;
        --lc-dur-slow: 1ms;
    }
}
`;
}

// 结构层 token 的**宿主文档**注入（幂等）。三个消费方：
//   1. 非 Shadow DOM 降级路径（老浏览器，widget 直接挂进宿主 DOM）；
//   2. 独立 chat.html / know.html 页面（没有 shadow root，:host 不匹配）；
//   3. widget.html 宿主页本身（骨架屏之外的容器层）。
// 幂等靠固定 id：重复 init/destroy 不会堆叠 <style>。这是本文件里唯一碰 DOM 的
// 函数，模块顶层仍然零副作用（node --test 直接 import 不会触发它）。
export const STRUCTURE_STYLE_ELEMENT_ID = 'lc-structure-tokens';

export function ensureStructureTokenStyle(doc) {
    const d = doc || (typeof document !== 'undefined' ? document : null);
    if (!d || !d.head || d.getElementById(STRUCTURE_STYLE_ELEMENT_ID)) return null;
    const el = d.createElement('style');
    el.id = STRUCTURE_STYLE_ELEMENT_ID;
    el.textContent = structureTokensCssText(':root');
    d.head.appendChild(el);
    return el;
}

// inline 模式（widget.html fill/centered、iframe、原生 WebView）根容器要不要给一个
// 硬性 minHeight 兜底（历史默认 '520px'）。
//
// 只在"宿主可能没给我们真实高度"时才需要——widget.html 自己的 CSS 链（html/
// body/#chatly-container 都是明确的 height:100%/dvh）永远提供真实高度，
// #chatly-container 又 overflow:hidden。CSS flexbox 的 min-height 是硬地板，
// flex-shrink 越不过去：键盘弹出时可视高度可能远小于 520px（键盘高度本身因
// 设备/输入法而异，不是个固定阈值），520 的地板会顶破这条真实链路，把面板撑
// 高到 520px，溢出部分被 #chatly-container 的 overflow:hidden 裁掉——真机反馈
// 正是 Android WebView fill 布局下键盘一弹，composer/"Powered by" 被裁掉半截。
//
// 只有"宿主环境没有 widget.html 这层、没法确定对方给没给我们高度"（第三方直接
// 把 JS SDK inline 模式挂进自己页面的任意 div）时，才需要这个兜底防止零高度
// 塌陷——调用方用 `#chatly-container` 元素是否存在（widget.html 独有的挂载点
// id）来判定 hasReliableHeightChain，widget.js（React inline 根 div + 骨架）和
// widget-index.js（shadow 内 .widget-root 挂载点）两处独立设置同一个真容器的
// minHeight，判据必须一致，所以唯一决策出口收在这里。
// @returns {string|undefined} undefined = 不设置 minHeight（交给真实高度链）。
export function resolveInlineMinHeightFallback({ hasReliableHeightChain, configuredMinHeight }) {
    if (hasReliableHeightChain) {
        return undefined;
    }
    return configuredMinHeight || '520px';
}

// /v1/chat 初始化失败时该给访客看哪种错误页（唯一分类出口——preInitClient 的
// 成功/失败分支都要喂同一份判据，不各自散判）。
//
// 三类，UI/文案分开（server-error 的按钮行为跟 network 一样都是"重试"，但
// 走独立文案，不是完全同一份拷贝）：
//   'app_unavailable' — app_id 不存在/缺失（服务端业务码 400/404）。这不是
//     访客能解决的问题，不给重试按钮——重试只会拿到同一个错误。技术细节
//     （比如具体是哪个 app_id）只打 console.error 给集成方看，页面上只说
//     访客能懂的话（"聊天暂时不可用"），不出现 app_id/code 这类术语。
//   'network' — 连接层失败（fetch reject/超时），跟 domain-failover 用的
//     判据同一套语义："还没连上"，不是"连上了但服务端说不行"。给重试按钮。
//   'server' — 服务端 5xx（HTTP 状态码本身，不是业务 code）。给重试按钮。
//   null — 其它情况（比如 code=403 访客被封/bot）不在这三类范围内，调用方
//     保持现状不介入（这不是本次任务要处理的场景）。
//
// 判定顺序：业务码优先于连接层信号——一个连接失败之后重试又拿到了业务
// 400/404，那就是"连上了，但这个 app 真不存在"，不该继续按 network 处理。
export function resolveInitErrorKind({ businessCode, isConnectionFailure, httpStatus }) {
    if (businessCode === 400 || businessCode === 404) {
        return 'app_unavailable';
    }
    if (isConnectionFailure) {
        return 'network';
    }
    if (typeof httpStatus === 'number' && httpStatus >= 500) {
        return 'server';
    }
    return null;
}

// ============================================================================
// 品牌色结构性闸门（"抢跑蓝"终局修复）——唯一决策出口。
//
// 不变式（用户原话反馈"有概率"看到默认蓝按钮/widget，已经打了三轮补丁仍
// 复发，本次要把它做成代码里可执行的规则，而不是逐个入口堵）：
//
//   在本 app 的 widget_config 已知之前，任何带品牌色的 UI 一个像素都不许
//   绘制。允许绘制的只有中性骨架（灰系，无品牌色）。工厂默认配色只有一种
//   合法出场方式：服务端明确返回了"该商户无自定义配置"。
//
// "已知"只有三种合法来源，见 resolveColorKnown 的三个参数：
//   1. hasExplicitCustomization —— 集成方在 window.laneChatCustomizations
//      显式配置过 mainColor，不需要等网络，立即已知。
//   2. hasCachedConfig —— 本地 localStorage 存过这个 app_id 上次成功拿到的
//      widget_config（见 widget.js 的 lane_widget_config_<appId> 读写）。
//      回访场景下这个值几乎总是 true，直接用缓存色首绘，不再有等待窗口。
//   3. serverConfirmed —— /v1/chat 这次请求已经返回（preInitClient 的 try
//      块跑完，不管服务端说的是"有自定义色"还是"没配过用工厂色"，只要是
//      服务端明确给出的答案就算数）。
//
// 故意不包含的第四种来源："客户端超时/放弃等待"。之前 fabColorResolved
// 用一个 4s setTimeout 兜底"没等到就当已解决"——这正是复发的根因之一：
// 超时之后径直把工厂蓝当成"已确认"的颜色画出来，而它根本不是服务端确认过
// 的值。真正网络broken的场景现在有 resolveInitErrorKind/renderInitError
// 兜底（渲染错误页，不渲染任何品牌色 UI），不需要靠"超时就画默认色"这种
// 会违反不变式的手段来避免骨架转不出去。
//
// 一旦为 true 就不会变回 false（跟 configReady 一样是单调状态）——调用方
// (widget.js render()) 只在两处渲染入口（inline 分支、浮动分支）各查一次
// 这个总闸，不在每个具体元素（FAB/header/teaser/消息气泡...）里各自判断，
// 这样新增的品牌色渲染点天然就在闸门保护范围内，不需要记得"这里也要判一下"。
export function resolveColorKnown({ hasExplicitCustomization, hasCachedConfig, serverConfirmed }) {
    return !!(hasExplicitCustomization || hasCachedConfig || serverConfirmed);
}

// appConfig 本地缓存（localStorage lane_widget_config_<appId>）的 schema 版本
// ——存进值里而不是 key 里，改缓存形状时旧版本的值读出来直接判无效，不需要
// 挨个失效旧 key。缓存的是 /v1/chat 响应里完整的 appConfig（不只是
// appConfig.widget_config 那一小块）——回访场景要"彻底消灭等待窗口"，不只是
// 颜色对，configReady 本身也要能从缓存直接翻真，appConfig 里 welcome_message/
// service_title 这些非颜色字段同样得有值可用，不然面板"颜色对了但内容区还在转"，
// 等待窗口只是变窄没有消灭。widget.js 的 Widget._readCachedAppConfig 是唯一
// 读者，用的就是这个常量 + 下面这个纯解析函数。
export const WIDGET_CONFIG_CACHE_SCHEMA_VERSION = 1;

/**
 * 把 localStorage 里读到的原始字符串解析成缓存的 appConfig 对象，或者在任何
 * 一步不对时返回 null（读不出来/JSON 坏了/版本不match/形状不对）——脏缓存
 * 一律当没缓存，回退到"等服务端"这条本来就存在的路径，不能让它打断首绘。
 * 纯函数（不摸 localStorage 本身——那部分留给调用方，这里只测"给一个字符串，
 * 该不该采信"这条判断逻辑）。
 */
export function parseCachedAppConfig(raw, expectedSchemaVersion = WIDGET_CONFIG_CACHE_SCHEMA_VERSION) {
    if (typeof raw !== 'string' || raw === '') return null;
    let parsed;
    try {
        parsed = JSON.parse(raw);
    } catch (e) {
        return null;
    }
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.schemaVersion !== expectedSchemaVersion) return null;
    if (!parsed.appConfig || typeof parsed.appConfig !== 'object') return null;
    return parsed.appConfig;
}
